import { useState, useEffect, useRef } from 'react';
import { Form, Row, Col, Card, Button, Modal } from 'react-bootstrap';
import { ProductInfo, ProductType } from '../../models';
import { useLocaleContext } from '../../i18n';

export type ProductEntry = ProductInfo & { _id: number };

export const PRODUCT_TYPES: ProductType[] = ['GEL', 'BAR', 'DRINK', 'CHEW', 'GUMMY', 'FOOD'];
export const DIVISIBLE_TYPES = new Set<ProductType>(['DRINK', 'CHEW', 'GUMMY']);

const TYPE_COLORS: Record<ProductType, string> = {
  DRINK: '#0d6efd',
  GEL: '#6f42c1',
  FOOD: '#198754',
  BAR: '#6B4423',
  CHEW: '#fd7e14',
  GUMMY: '#fd7e14',
};

export function defaultMinPortion(type: ProductType, carbs: number): number {
  if (type === 'DRINK') return Math.max(1, Math.round(carbs / 4));
  if (type === 'GUMMY') return carbs;
  return 5;
}

interface Props {
  entry: ProductEntry;
  onUpdate: (id: number, field: keyof ProductInfo, value: string | number | boolean) => void;
  onRemove: (id: number) => void;
  autoEdit?: boolean;
}

function Stepper({
  value,
  onChange,
  min = 0,
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(value));

  const commit = () => {
    const num = Math.max(min, parseInt(editValue, 10) || value);
    onChange(num);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Form.Control
        type="number"
        min={min}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          else if (e.key === 'Escape') setIsEditing(false);
        }}
        autoFocus
        className="no-spinners"
        style={{
          width: 60,
          textAlign: 'center',
          fontSize: '1rem',
          fontWeight: 'bold',
          padding: '0.25rem',
        }}
      />
    );
  }

  return (
    <div className="km-stepper">
      <button
        className="km-stepper-btn"
        onClick={() => onChange(Math.max(min, value - 1))}
      >
        −
      </button>
      <span
        className="km-stepper-value"
        onClick={() => {
          setEditValue(String(value));
          setIsEditing(true);
        }}
      >
        {value}
      </span>
      <button className="km-stepper-btn" onClick={() => onChange(value + 1)}>
        +
      </button>
    </div>
  );
}

export default function ProductRow({ entry, onUpdate, onRemove, autoEdit }: Props) {
  const { locale } = useLocaleContext();
  const isDivisible = DIVISIBLE_TYPES.has(entry.type);
  const isVerified = entry.verified ?? false;
  const isActive = entry.active ?? true;
  const isDisabled = (entry.availableQuantity ?? 1) === 0;
  const [isEditing, setIsEditing] = useState(autoEdit ?? false);
  const [draft, setDraft] = useState<ProductInfo>({ ...entry });
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoEdit && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [autoEdit]);

  const save = () => {
    (Object.keys(draft) as (keyof ProductInfo)[]).forEach((field) => {
      if (draft[field] !== entry[field]) {
        onUpdate(entry._id, field, draft[field] as string | number);
      }
    });
    setIsEditing(false);
  };

  const cancel = () => {
    if (autoEdit && !entry.name) {
      onRemove(entry._id);
    } else {
      setDraft({ ...entry });
      setIsEditing(false);
    }
  };

  const toggleActive = () => {
    onUpdate(entry._id, 'active', isActive ? false : true);
  };

  if (isEditing) {
    return (
      <>
        {/* Edit mode - Desktop */}
        <div className="d-none d-md-block" style={{ position: 'relative' }}>
          <Row className="g-2 align-items-center mb-1" style={{ paddingRight: '80px' }}>
            <Col md={3}>
              <Form.Control
                ref={nameInputRef}
                placeholder={locale.planner.products.placeholderName}
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                size="sm"
              />
            </Col>
            <Col md={2}>
              <Stepper
                value={draft.carbs}
                onChange={(v) => setDraft({ ...draft, carbs: v })}
              />
            </Col>
            <Col md={1}>
              <Form.Select
                value={draft.type}
                onChange={(e) => setDraft({ ...draft, type: e.target.value as ProductType })}
                size="sm"
              >
                {PRODUCT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Stepper
                value={draft.availableQuantity ?? 1}
                onChange={(v) => setDraft({ ...draft, availableQuantity: v })}
                min={0}
              />
            </Col>
            <Col md={2}>
              {DIVISIBLE_TYPES.has(draft.type) ? (
                <Stepper
                  value={draft.minDivisiblePortionGrams ?? 1}
                  onChange={(v) => setDraft({ ...draft, minDivisiblePortionGrams: v })}
                  min={1}
                />
              ) : (
                <div className="text-center text-muted" style={{ fontSize: '0.9rem' }}>
                  {locale.planner.products.whole}
                </div>
              )}
            </Col>
          </Row>
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              gap: '4px',
            }}
          >
            <Button size="sm" variant="success" onClick={save} title={locale.planner.products.save} disabled={!draft.name.trim()}>
              ✓
            </Button>
            <Button size="sm" variant="secondary" onClick={cancel} title={locale.planner.products.cancel}>
              ✗
            </Button>
          </div>
        </div>

        {/* Edit mode - Mobile */}
        <div className="d-md-none" style={{ position: 'relative' }}>
          <Card className="card-dark-compact mb-2">
            <Card.Header className="p-2">
              <Row className="align-items-center">
                <Col>
                  <Form.Control
                    placeholder="Name"
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    size="sm"
                  />
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="p-2">
              <Row className="g-2 mb-2">
                <Col xs={6}>
                  <small className="text-secondary">{locale.planner.products.carbsUnit}</small>
                  <div style={{ marginTop: '0.25rem' }}>
                    <Stepper
                      value={draft.carbs}
                      onChange={(v) => setDraft({ ...draft, carbs: v })}
                    />
                  </div>
                </Col>
                <Col xs={6}>
                  <small className="text-secondary">Type</small>
                  <div style={{ marginTop: '0.25rem' }}>
                    <Form.Select
                      value={draft.type}
                      onChange={(e) => setDraft({ ...draft, type: e.target.value as ProductType })}
                      size="sm"
                    >
                      {PRODUCT_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                </Col>
              </Row>
              <Row className="g-2 mb-2">
                <Col xs={6}>
                  <small className="text-secondary">{locale.planner.products.qtyUnit}</small>
                  <div style={{ marginTop: '0.25rem' }}>
                    <Stepper
                      value={draft.availableQuantity ?? 1}
                      onChange={(v) => setDraft({ ...draft, availableQuantity: v })}
                      min={0}
                    />
                  </div>
                </Col>
                {DIVISIBLE_TYPES.has(draft.type) && (
                  <Col xs={6}>
                    <small className="text-secondary">{locale.planner.products.minPortionUnit}</small>
                    <div style={{ marginTop: '0.25rem' }}>
                      <Stepper
                        value={draft.minDivisiblePortionGrams ?? 1}
                        onChange={(v) => setDraft({ ...draft, minDivisiblePortionGrams: v })}
                        min={1}
                      />
                    </div>
                  </Col>
                )}
              </Row>
              <div className="d-flex gap-2">
                <Button variant="success" onClick={save} className="flex-grow-1">
                  {locale.planner.products.save}
                </Button>
                <Button variant="secondary" onClick={cancel} className="flex-grow-1">
                  {locale.planner.products.cancel}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </div>
      </>
    );
  }

  // View mode
  return (
    <>
      {/* View mode - Desktop */}
      <div
        className="d-none d-md-block"
        style={{
          position: 'relative',
          opacity: (isDisabled || !isActive) ? 0.4 : 1,
          filter: (isDisabled || !isActive) ? 'grayscale(100%)' : 'none',
          transition: 'opacity 0.2s, filter 0.2s',
        }}
      >
        <Row className="g-2 align-items-center mb-1" style={{ paddingRight: '80px' }}>
          <Col md={3}>
            <button
              type="button"
              onClick={toggleActive}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                backgroundColor: 'transparent',
                border: 'none',
                padding: '0.25rem 0.5rem',
                marginLeft: '-0.5rem',
                cursor: 'pointer',
                borderRadius: '4px',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              title={isActive ? 'Deaktywuj produkt' : 'Aktywuj produkt'}
            >
              <i
                className={`bi ${isActive ? 'bi-backpack-fill' : 'bi-backpack'}`}
                style={{
                  color: isActive ? '#198754' : '#6c757d',
                  fontSize: '1.1rem',
                  transition: 'color 0.2s',
                }}
              ></i>
              <span className="text-light" style={{ opacity: isActive ? 1 : 0.5, transition: 'opacity 0.2s' }}>
                {entry.name}
                {isVerified && (
                  <i
                    className="bi bi-patch-check-fill"
                    style={{ color: '#198754', marginLeft: '0.5rem', fontSize: '0.9rem' }}
                    title={locale.planner.products.verified}
                  ></i>
                )}
              </span>
            </button>
          </Col>
          <Col md={2}>
            <span className="text-light">{entry.carbs}g</span>
          </Col>
          <Col md={1}>
            <button
              className="btn btn-sm"
              style={{
                backgroundColor: TYPE_COLORS[entry.type],
                color: '#fff',
                border: 'none',
                padding: '0.25rem 0.5rem',
                fontSize: '0.85rem',
                fontWeight: '600',
                cursor: 'default',
                borderRadius: '4px',
              }}
            >
              {entry.type}
            </button>
          </Col>
          <Col md={2}>
            <Stepper
              value={entry.availableQuantity ?? 1}
              onChange={(v) => onUpdate(entry._id, 'availableQuantity', v)}
              min={0}
            />
          </Col>
          <Col md={2}>
            <div className="text-center text-light" style={{ fontSize: '0.9rem' }}>
              {isDivisible ? `${entry.minDivisiblePortionGrams}g` : locale.planner.products.whole}
            </div>
          </Col>
        </Row>
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            gap: '4px',
          }}
        >
          {!isVerified && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setIsEditing(true)}
              title={locale.planner.products.edit}
            >
              <i className="bi bi-pencil"></i>
            </button>
          )}
          {isVerified ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-success"
              onClick={() => setShowPartnerModal(true)}
              title={locale.planner.products.partnerStore}
            >
              <i className="bi bi-bag-check"></i>
            </button>
          ) : (
            <button
              type="button"
              className="btn delete-icon-btn"
              onClick={() => onRemove(entry._id)}
              title={locale.planner.products.delete}
            >
              <i className="bi bi-trash"></i>
            </button>
          )}
        </div>
      </div>

      {/* View mode - Mobile */}
      <div
        className="d-md-none"
        style={{
          position: 'relative',
          opacity: (isDisabled || !isActive) ? 0.4 : 1,
          filter: (isDisabled || !isActive) ? 'grayscale(100%)' : 'none',
          transition: 'opacity 0.2s, filter 0.2s',
        }}
      >
        <Card className="card-dark-compact mb-2">
          <Card.Header className="d-flex align-items-center justify-content-between p-2">
            <button
              type="button"
              onClick={toggleActive}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                backgroundColor: 'transparent',
                border: 'none',
                padding: '0.5rem',
                marginLeft: '-0.5rem',
                cursor: 'pointer',
                borderRadius: '4px',
                transition: 'background-color 0.2s',
                flex: 1,
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              title={isActive ? 'Deaktywuj produkt' : 'Aktywuj produkt'}
            >
              <i
                className={`bi ${isActive ? 'bi-backpack-fill' : 'bi-backpack'}`}
                style={{
                  color: isActive ? '#198754' : '#6c757d',
                  fontSize: '1.2rem',
                  transition: 'color 0.2s',
                  flexShrink: 0,
                }}
              ></i>
              <div>
                <div className="fw-bold text-light" style={{ opacity: isActive ? 1 : 0.5, transition: 'opacity 0.2s' }}>
                  {entry.name}
                  {isVerified && (
                    <i
                      className="bi bi-patch-check-fill"
                      style={{ color: '#198754', marginLeft: '0.5rem', fontSize: '0.85rem' }}
                      title={locale.planner.products.verified}
                    ></i>
                  )}
                </div>
                  <button
                    className="btn btn-sm mt-1"
                    style={{
                      backgroundColor: TYPE_COLORS[entry.type],
                      color: '#fff',
                      border: 'none',
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      cursor: 'default',
                      borderRadius: '4px',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {entry.type}
                  </button>
                </div>
            </button>
            <div>
              {!isVerified && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary me-1"
                  onClick={() => setIsEditing(true)}
                  title="Edytuj produkt"
                >
                  <i className="bi bi-pencil"></i>
                </button>
              )}
              {isVerified ? (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-success"
                  onClick={() => setShowPartnerModal(true)}
                  title="Przejdź do sklepu partnera"
                  style={{ padding: '4px' }}
                >
                  <i className="bi bi-bag-check"></i>
                </button>
              ) : (
                <button
                  type="button"
                  className="btn delete-icon-btn"
                  onClick={() => onRemove(entry._id)}
                  title="Usuń produkt"
                  style={{ padding: '4px' }}
                >
                  <i className="bi bi-trash"></i>
                </button>
              )}
            </div>
          </Card.Header>
          <Card.Body className="p-2">
            <Row className="g-2 mb-2">
              <Col xs={6}>
                <small className="text-secondary">Carbs (g)</small>
                <div className="text-light">{entry.carbs}g</div>
              </Col>
              <Col xs={6}>
                <small className="text-secondary">Qty</small>
                <div style={{ marginTop: '0.25rem' }}>
                  <Stepper
                    value={entry.availableQuantity ?? 1}
                    onChange={(v) => onUpdate(entry._id, 'availableQuantity', v)}
                    min={0}
                  />
                </div>
              </Col>
            </Row>
            {isDivisible && (
              <Row>
                <Col xs={12}>
                  <small className="text-secondary">Min Portion (g)</small>
                  <div className="text-light">{entry.minDivisiblePortionGrams}g</div>
                </Col>
              </Row>
            )}
          </Card.Body>
        </Card>
      </div>

      <Modal show={showPartnerModal} onHide={() => setShowPartnerModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{entry.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {locale.planner.products.partnerModalText}
            <strong> {entry.name}</strong>.
          </p>
          <p className="mb-0 text-muted">
            <small>{locale.planner.products.partnerModalComingSoon}</small>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPartnerModal(false)}>
            Zamknij
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
